import { ItemTypes } from 'lib/dashboard/types';
import { objectToQsParam } from 'modules/utils/UrlUtils';
import { PatientEducationRelatedData, RelatedDataTypes } from 'serverTypes/event';
import { SnoozeOptions } from 'store/priorityItems/types';

/**
 * DeepLinks should contain a route for mobile and a path for web
 *
 */
export interface DeepLinkRouteDefinition {
  route: string;
  path: string;
  params?: Record<any, any>;
  amwell?: boolean;
}

type LinkGeneraterFunction = (
  id?: number | string,
  relatedData?: RelatedDataTypes
) => DeepLinkRouteDefinition;

interface AllowedItemsInterface {
  type: ItemTypes;
  payload?: {
    advisoryName?: string | null;
    advisoryReferenceCD?: string | null;
    advisoryType?: string;
    createdAt?: Date;
    eventId?: string | null;
    eventTypeId?: string;
    hiUuid?: string;
    id?: string;
    onAnalyticsClick?: () => void;
    onAnalyticsDismissedClick?: () => void;
    relatedData?: any;
    snoozeOptions?: SnoozeOptions;
    sourceId?: string;
    surrogateUuid?: string;
  };
}

export const getGoToIdIndex = (allowedItems: AllowedItemsInterface[], goToId: string) => {
  let firstItemIndex = allowedItems.findIndex(i => i?.payload?.eventId === goToId);

  // if we couldnt match on `eventId` above, we'll try on `id`
  if (firstItemIndex === -1) {
    firstItemIndex = allowedItems.findIndex(i => i?.payload?.id === goToId);
  }

  // If we still couldnt match, we'll try `surrogateUuid`.
  // We use this as the goToId when it's present.
  // Health advisories have them when Config.HIGH_PRIORITY_ITEMS_V2 is enabled.
  if (firstItemIndex === -1) {
    firstItemIndex = allowedItems.findIndex(i => i?.payload?.surrogateUuid === goToId);
  }

  return firstItemIndex < 0 ? 0 : firstItemIndex;
};

const getMedicationRouteLink: LinkGeneraterFunction = (id, relatedData) => {
  if (Number(id) > 0) {
    let path = `/u/health-record/current-medications/medications/${id}`;
    if (relatedData?.mhpDetails) {
      path = `/u/health-record/current-medications/medications/${id}/${objectToQsParam(
        relatedData.mhpDetails
      )}`;
    }
    return {
      path,
      route: `MedicationDetailsSummary`,
      params: {
        id,
        mhpDetails: relatedData?.mhpDetails || {}
      }
    };
  }

  return {
    route: `MedicationsScreen`,
    path: `/u/health-record/current-medications/medications`,
    params: { medicationType: 'current' }
  };
};

const getVisitSummaryRouteLink: LinkGeneraterFunction = (id, relatedData) => {
  if (Number(id) > 0) {
    let path = `/u/health-record/visit-summaries/live/fhir/${id}`;
    if (relatedData?.mhpDetails) {
      path = `/u/health-record/visit-summaries/live-detailed/millenium/${id}/${objectToQsParam(
        relatedData.mhpDetails
      )}`;
    }
    return {
      path,
      route: `VisitSummaryLive`,
      params: {
        source: 'fhir',
        id,
        mhpDetails: relatedData?.mhpDetails
      }
    };
  }

  return {
    path: '/u/health-record/visit-summaries',
    route: `VisitSummarySearch`
  };
};

const getGoalsRouteLink: LinkGeneraterFunction = () => ({
  route: 'HEALTH_ROOT',
  path: '/u/health-record',
  params: {
    expand: 'goals'
  }
});

const getUpcomingAppointmentRouteLink: LinkGeneraterFunction = id => ({
  route: `CARE_ROOT`,
  path: '/u/appointments',
  params: { isExpanded: 'BOOKING' }
});

const getVideoVisitRouteLink: LinkGeneraterFunction = () => ({
  amwell: true,
  route: 'ConnectCareLanding',
  path: '/u/get-care-now/connect-care'
});

const getMessagingRouteLink: LinkGeneraterFunction = () => ({
  route: 'MESSAGE_ROOT',
  path: '/u/messages'
});

const getNewBillRouteLink: LinkGeneraterFunction = id => ({
  route: `BillingDetail`,
  path: `/u/billing/bill-detail/${id}`,
  params: {
    eaid: id
  }
});

const getUnpaidBillRouteLink: LinkGeneraterFunction = () => ({
  route: 'BillSummary',
  path: '/u/billing/bills'
});

const getDashboardRouteLink: LinkGeneraterFunction = (id, additionalParams) => ({
  route: 'HOME_ROOT',
  path: '/u/dashboard',
  params: {
    category: 'carousel',
    ...additionalParams
  }
});

const getTestResultsRouteLink: LinkGeneraterFunction = () => ({
  route: 'TestResultsHealthRecordsList',
  path: '/u/health-record/test-results'
});

const getDocumentRouteLink: LinkGeneraterFunction = () => ({
  route: 'ClinicNotes',
  path: '/u/health-record/clinic-notes-and-documents'
});

const getPatientEducationLink: LinkGeneraterFunction = (id, relatedData) => ({
  route: 'PatientEducationDetail',
  path: `/u/health-record/patient-education/detail/${
    (relatedData as PatientEducationRelatedData)?.metaData.accessCode
  }`,
  params: {
    educationItem: (relatedData as PatientEducationRelatedData)?.metaData
  }
});

const getDefaultRouteLink: LinkGeneraterFunction = () => ({
  route: 'HOME_ROOT',
  path: '/u/dashboard'
});

const getVisitPayRouteLink: LinkGeneraterFunction = () => ({
  route: 'BillSummary',
  path: '/u/billing/bills'
});

const getRegistrationCallbackLink = () => ({
  complete: 'register:complete',
  cancel: 'register:cancel'
});

const getAuthCallbackLink = () => ({
  close: 'auth-close'
});

export const RouteLinkGenerator = {
  document: getDocumentRouteLink,
  medication: getMedicationRouteLink,
  visitSummary: getVisitSummaryRouteLink,
  goals: getGoalsRouteLink,
  upcomingAppointment: getUpcomingAppointmentRouteLink,
  videoVisit: getVideoVisitRouteLink,
  messaging: getMessagingRouteLink,
  newBill: getNewBillRouteLink,
  unpaidBill: getUnpaidBillRouteLink,
  dashboard: getDashboardRouteLink,
  testResults: getTestResultsRouteLink,
  default: getDefaultRouteLink,
  visitPay: getVisitPayRouteLink,
  registrationCallback: getRegistrationCallbackLink,
  authCallback: getAuthCallbackLink,
  patientEducation: getPatientEducationLink
};
